import { useEffect, useState } from 'react';
import {
  usePartnerShortQuery,
  usePracticeCsvTemplateDownloadMutation,
  usePracticesCsvImportMutation,
} from '@common/cache';
import { SelectChangeEvent } from '@mui/material';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';

export const useImportPracticeModal = (handleClose: () => void) => {
  const { data: partnerOptions = [] } = usePartnerShortQuery();
  const {
    mutateAsync: templateMutateAsync,
    data: csvTemplate,
    isPending,
  } = usePracticeCsvTemplateDownloadMutation();
  const {
    mutateAsync: importMutateAsync,
    error,
    reset,
  } = usePracticesCsvImportMutation();
  const [partnerId, setPartnerId] = useState('');
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (csvTemplate) {
      const url = URL.createObjectURL(csvTemplate);

      const a = document.createElement('a');
      a.href = url;
      a.download = `practices template`;

      function handleOnDownload() {
        setTimeout(() => {
          URL.revokeObjectURL(url);
          a.removeEventListener('click', handleOnDownload);
        }, 150);
      }

      a.addEventListener('click', handleOnDownload, false);
      a.click();
    }
  }, [csvTemplate]);

  useEffect(() => {
    if (partnerOptions.length > 0) {
      setPartnerId(partnerOptions[0].id);
    }
  }, [partnerOptions]);

  const handleChangePartnerId = (event: SelectChangeEvent) => {
    setPartnerId(event.target.value);
  };

  const handleChangeFile = (newFile: File | null) => {
    setFile(newFile);
    reset();
  };

  const handleCloseAction = () => {
    handleClose();
    setFile(null);
  };

  const handleDownloadCSVTemplate = async () => {
    const loadingToast = toast.loading('Downloading CSV template...', {
      isLoading: true,
      autoClose: 500,
    });

    try {
      await templateMutateAsync(partnerId);
      toast.success('Downloaded successfully.');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      toast.update(loadingToast, { isLoading: false, autoClose: 1 });
    }
  };

  const handleImportCsvFile = async () => {
    const loadingToast = toast.loading('Importing CSV file...', {
      isLoading: true,
      autoClose: 500,
    });

    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      await importMutateAsync({
        file: formData,
        record_id: partnerId,
      });
      toast.success('Imported successfully.');
    } catch {
      toast.error('Something went wrong.');
    } finally {
      toast.update(loadingToast, { isLoading: false, autoClose: 1 });
    }
  };

  return {
    partnerOptions: partnerOptions.map(o => ({ value: o.id, label: o.name })),
    partnerId,
    handleChangePartnerId,
    handleDownloadCSVTemplate,
    isTemplateBtnDisabled: isPending,
    handleImportCsvFile,
    handleChangeFile,
    importError: error ? formatApiError(error).split(', ') : [],
    file,
    handleCloseAction,
  };
};
